import type { WorkspaceIntroCardProps } from '../../../shared/ui/common/WorkspaceIntroCard';
import {
  section,
  step,
  type QuickFillGuideContentArgs,
} from './quickFillGuideHelpers';

export const buildQuickFillGuideContent = ({
  visibleColumnCount,
  rowCount,
  workflowMode,
  isTableLoading,
}: QuickFillGuideContentArgs): WorkspaceIntroCardProps => ({
  eyebrow: 'Revit parameter editing',
  title: 'Use Quick Fill for controlled bulk edits in Revit',
  summary:
    'Quick Fill changes Revit parameter values across a defined set of rows and columns. PCF Mapping defines how those parameters populate PCF, BOM, ISO, and SKEY output, while Validation confirms the result.',
  hint:
    'Choose family/type or instance rows, keep the relevant identifier and target parameter columns visible, filter the table to the intended components, preview the edit, then save the changes and rerun Validation.',
  status: isTableLoading ? 'Loading Revit parameters' : 'Ready to edit Revit parameters',
  actionLabel: 'Start tour',
  chips: [
    `${visibleColumnCount} visible columns`,
    `${rowCount} Revit rows`,
    workflowMode === 'multipage' ? 'Wizard-selected columns' : 'User-selected columns',
    'Row, cell, and column edits',
  ],
  sections: [
    section('Use this when', [
      'A known Revit parameter needs the same value corrected across selected family/type rows or instance rows.',
      'Validation has identified missing or inconsistent source values, and Mapping already defines how those Revit parameters map to PCF output.',
      'You need to make traceable cell, row, column, Auto-fill, Find and Replace, or CSV edits without changing the mapping configuration.',
    ]),
    section('Not for', [
      'Do not use Quick Fill to define PCF attributes, formulas, fallback rules, SKEY mappings, BOM logic, or ISO output rules. Configure these in Mapping.',
      'Do not apply the same value across rows or columns when its engineering meaning differs by family, type, instance, system, or component.',
    ]),
    section('Focus during this review', [
      'Use family/type rows for shared type values and instance rows for values that differ between placed elements.',
      'Keep row identifiers visible alongside the editable parameter columns you are reviewing. Hiding a column does not remove the parameter from Revit.',
      'Filter the table first, then select the cells or rows you want to edit. Column-wide actions affect that parameter across all rows in the current table view.',
      'Use Select in Revit to verify the corresponding model elements, and use markers to keep important exceptions visible while filtering the table.',
      'Preview Auto-fill, imported, or other bulk changes before applying them. Use Undo if unintended cells are changed.',
    ]),
    section('Handoff', [
      'Save applies eligible edits to Revit. Review any skipped, blocked, or failed rows before continuing.',
      'Return to Mapping if the wrong Revit parameter, PCF destination, formula, or fallback is controlling the result.',
      'Rerun Validation to confirm that the corrected source values produce the expected PCF, BOM, ISO, and SKEY output.',
    ]),
  ],
  steps: [
    step('1. Confirm where the change belongs', 'Verify that the task is a Revit value edit, not a PCF mapping change.', [
      'The target Revit parameter and expected engineering value are known.',
      'Mapping already defines the downstream PCF, BOM, ISO, or SKEY rule.',
    ]),
    step('2. Define the rows and columns to edit', 'Choose family/type or instance rows, show the relevant identifiers and target parameters, then filter the table to the intended components.', [
      'The selected rows represent the Revit elements or types you intend to edit.',
      'The editable columns correspond to the parameters you intend to change.',
    ]),
    step('3. Inspect before editing', 'Compare the selected rows, use Select in Revit when an element is difficult to identify, and mark exceptions for separate handling.', [
      'The selected rows represent the same engineering case.',
      'Exceptions are excluded or marked before applying a bulk action.',
    ]),
    step('4. Apply the narrowest edit', 'Edit a single cell, fill a selected range, update a column, or use Auto-fill, Find and Replace, or CSV import only when the preview matches the intended cells.', [
      'The action affects only the selected rows, cells, or columns.',
      'Undo is available if the result is not as expected.',
    ]),
    step('5. Save and validate downstream', 'Review the results of the Revit updates, resolve any skipped or failed changes, then rerun Validation.', [
      'Eligible values were written to the intended Revit elements.',
      'Any skipped or failed rows have been reviewed and resolved.',
      'PCF, BOM, ISO, and SKEY validation results now match the configured mappings.',
    ]),
  ],
});
